/**
 * Editor for one direction (up or down) of a Pool's `Dynamics`.
 *
 * The autoscaler waits `stabilization_seconds` of continuous SLO state
 * before acting, then applies a `step` (replicas or %), then refuses
 * to act for `cooldown_seconds`. See
 * `controller/controller/autoscaler/state.py` for the in-memory state
 * machine that consumes these values.
 */
import type { Dynamics } from "@/api/types";

interface DynamicsEditorProps {
  /** Title for the surrounding context (e.g. "Scale up", "Scale down"). */
  direction: "up" | "down";
  value: Dynamics;
  onChange: (next: Dynamics) => void;
  disabled?: boolean;
}

const DIRECTION_LABEL: Record<"up" | "down", string> = {
  up: "Scale up",
  down: "Scale down",
};

const DIRECTION_HINT: Record<"up" | "down", string> = {
  up: "How quickly the autoscaler reacts to SLO violations by adding capacity.",
  down: "How quickly the autoscaler reacts to SLO recovery by removing capacity. Down-scaling targets the lowest-priority pool first.",
};

export function DynamicsEditor({
  direction,
  value,
  onChange,
  disabled,
}: DynamicsEditorProps) {
  const setField = <K extends keyof Dynamics>(
    key: K,
    next: Dynamics[K],
  ) => onChange({ ...value, [key]: next });

  const stepMode: "replicas" | "percent" =
    value.step_percent != null ? "percent" : "replicas";

  return (
    <div className="dynamics-editor">
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          color: "var(--ink-faint)",
          marginBottom: 6,
        }}
      >
        {DIRECTION_LABEL[direction]}
      </div>
      <p className="muted" style={{ fontSize: 11.5, margin: "0 0 12px" }}>
        {DIRECTION_HINT[direction]}
      </p>
      <div className="form-grid">
        <label>stabilization (s)</label>
        <div className="field">
          <input
            type="number"
            min={0}
            value={value.stabilization_seconds}
            onChange={(e) =>
              setField("stabilization_seconds", Number(e.target.value))
            }
            disabled={disabled}
            style={{ width: 120 }}
          />
          <div className="field-hint">
            Continuous-violation window before the autoscaler acts. Longer =
            more conservative.
          </div>
        </div>
        <label>step</label>
        <div className="field">
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <select
              value={stepMode}
              onChange={(e) => {
                const next = e.target.value as "replicas" | "percent";
                if (next === "replicas") {
                  onChange({
                    ...value,
                    step_percent: null,
                    step_replicas: value.step_replicas ?? 1,
                  });
                } else {
                  onChange({
                    ...value,
                    step_replicas: null,
                    step_percent: value.step_percent ?? 25,
                  });
                }
              }}
              disabled={disabled}
            >
              <option value="replicas">replicas</option>
              <option value="percent">percent</option>
            </select>
            {stepMode === "replicas" ? (
              <input
                type="number"
                min={1}
                value={value.step_replicas ?? 1}
                onChange={(e) =>
                  setField("step_replicas", Number(e.target.value))
                }
                disabled={disabled}
                style={{ width: 90 }}
              />
            ) : (
              <input
                type="number"
                min={1}
                max={100}
                value={value.step_percent ?? 25}
                onChange={(e) =>
                  setField("step_percent", Number(e.target.value))
                }
                disabled={disabled}
                style={{ width: 90 }}
              />
            )}
            {stepMode === "percent" && (
              <span className="muted" style={{ fontSize: 12 }}>
                %
              </span>
            )}
          </div>
          <div className="field-hint">
            How many replicas to add (or remove) per fire. Mutually
            exclusive — pick replicas xor percent.
          </div>
        </div>
        <label>cooldown (s)</label>
        <div className="field">
          <input
            type="number"
            min={0}
            value={value.cooldown_seconds}
            onChange={(e) =>
              setField("cooldown_seconds", Number(e.target.value))
            }
            disabled={disabled}
            style={{ width: 120 }}
          />
          <div className="field-hint">
            Refractory period after each fire so capacity changes can land
            before the next decision.
          </div>
        </div>
      </div>
    </div>
  );
}

export const DEFAULT_UP_DYNAMICS: Dynamics = {
  stabilization_seconds: 60,
  step_replicas: 1,
  step_percent: null,
  cooldown_seconds: 120,
};

export const DEFAULT_DOWN_DYNAMICS: Dynamics = {
  stabilization_seconds: 300,
  step_replicas: 1,
  step_percent: null,
  cooldown_seconds: 300,
};
